import React from "react";
import { Link } from "react-router-dom";
import { useSettings } from "../context/SettingsContext";
import { useAuth } from "../context/AuthContext";
import { usePublicAuth } from "../context/PublicAuthContext";

function MaintenancePage({ message, logoUrl, siteName }) {
  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center px-6 text-center"
      style={{
        background: "#050505",
        backgroundImage:
          "radial-gradient(circle at 50% 40%, rgba(225,29,72,0.08) 0%, transparent 60%)",
      }}
    >
      {/* Animated gear */}
      <div className="relative mb-8">
        <div
          className="w-20 h-20 rounded-full border-4 border-zinc-800 flex items-center justify-center"
          style={{ animation: "spin 8s linear infinite" }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
            className="w-10 h-10 text-rose-600">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
          </svg>
        </div>
        <div
          className="absolute -inset-2 rounded-full border border-rose-600/20"
          style={{ animation: "pulse 2s ease-in-out infinite" }}
        />
      </div>

      {/* Text */}
      <p className="text-rose-500 text-xs font-bold uppercase tracking-widest mb-3">
        Wartungsarbeiten
      </p>
      <h1 className="text-3xl sm:text-4xl font-black text-white font-heading mb-4 leading-tight">
        Wir sind gleich<br />wieder da
      </h1>
      <p className="text-zinc-400 text-sm sm:text-base max-w-md leading-relaxed mb-8">
        {message || "Wir führen gerade Wartungsarbeiten durch. Wir sind gleich wieder für euch da!"}
      </p>

      {/* Divider */}
      <div className="flex items-center gap-3 mb-8">
        <div className="h-px w-12 bg-white/10" />
        <div className="w-1.5 h-1.5 rounded-full bg-rose-600" />
        <div className="h-px w-12 bg-white/10" />
      </div>

      {/* Branding */}
      <div className="flex items-center gap-2.5">
        {logoUrl ? (
          <img src={logoUrl} alt={siteName || "HUWND"} className="h-8 w-auto object-contain opacity-80" />
        ) : (
          <>
            <div className="w-7 h-7 bg-rose-600 rounded-md flex items-center justify-center flex-shrink-0">
              <span className="text-white font-black text-xs font-heading">H</span>
            </div>
            <span className="font-black text-zinc-400 text-sm tracking-tight font-heading">{siteName || "HUWND"}</span>
          </>
        )}
      </div>

      {/* Admin Login Link (discreet) */}
      <Link
        to="/login"
        className="mt-10 text-zinc-800 hover:text-zinc-600 text-xs transition-colors"
        data-testid="maintenance-admin-link"
      >
        Admin
      </Link>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 0.3; transform: scale(1); } 50% { opacity: 0.7; transform: scale(1.05); } }
      `}</style>
    </div>
  );
}

export default function MaintenanceGate({ children }) {
  const { settings, loading } = useSettings();
  const { user } = useAuth();
  const { publicUser } = usePublicAuth();

  // Während Settings laden: nichts blockieren (kein Flash für Admins)
  if (loading) return children;

  // Admin/Developer kann Maintenance-Mode über BEIDE Auth-Kontexte bypassen
  const isPrivileged =
    user?.role === "admin" || user?.role === "developer" ||
    publicUser?.role === "admin" || publicUser?.role === "developer";

  if (settings?.maintenance_mode && !isPrivileged) {
    return <MaintenancePage message={settings.maintenance_message} logoUrl={settings.logo_url} siteName={settings.site_name} />;
  }

  return children;
}
